import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { genericErrorMessage } from '@/common/constants';

function isJSON(str: string): boolean {
  try {
    JSON.parse(str);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return false;
  }
  return true;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Converts a string to title case (capitalizes first letter of each word)
 * e.g. "DASHBOARD TESTING" -> "Dashboard Testing"
 */
export function toTitleCase(str: string | null | undefined): string {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function getGroupDisplayName(
  group: { pvGroupID: number; pvGroupName: string; pvGroupTitle?: string } | undefined | null
): string {
  if (!group) return '';
  if (group.pvGroupID === 1) return 'SCEMD';
  return group.pvGroupTitle || group.pvGroupName;
}

export function FormatCatchErrorMsg(error: unknown, customMessage?: string): string {
  let message: unknown = customMessage ? customMessage : genericErrorMessage;
  if (error instanceof Error) message = error.message;
  else {
    if (typeof error === 'object' && error !== null) {
      if ('message' in error) {
        message = error.message;
      } else if ('errorMessage' in error) {
        message = error.errorMessage;
      } else if ('data' in error) {
        if (typeof error.data === 'object' && error.data !== null) {
          if ('message' in error.data) {
            message = error.data.message;
          } else if ('errorMessage' in error.data) {
            message = error.data.errorMessage;
          }
        }
      }
    }
  }

  if (typeof message === 'object' && message !== null) {
    if ('message' in message) {
      message = message.message;
    } else if ('errorMessage' in message) {
      message = message.errorMessage;
    }
  } else if (typeof message === 'string') {
    if (isJSON(message)) {
      const errorMessage = JSON.parse(message as string);
      if (typeof errorMessage === 'object' && errorMessage !== null) {
        if ('message' in errorMessage) {
          message = errorMessage.message;
        } else if ('errorMessage' in errorMessage) {
          message = errorMessage.errorMessage;
        }
      }
    }
  }
  return JSON.stringify(message);
}

/**
 * Format a ZIP code string to US format (12345 or 12345-6789)
 * Strips non-digits and formats with hyphen for ZIP+4
 * @param value - Raw input string
 * @returns Formatted ZIP code
 */
export function formatZipCode(value: string | undefined | null): string {
  if (!value) return '';

  // Strip all non-digits
  const digits = value.replace(/\D/g, '');

  // Format based on length
  if (digits.length <= 5) {
    return digits;
  }

  // ZIP+4 format: 12345-6789
  return `${digits.slice(0, 5)}-${digits.slice(5, 9)}`;
}

/**
 * Format ZIP code as user types (for input masking)
 * Limits to 9 digits max and auto-inserts hyphen
 * @param value - Raw input string
 * @returns Formatted ZIP code for display in input
 */
/**
 * Format a phone number as 888-888-8888
 * Strips non-digits and inserts hyphens. Limits to 10 digits.
 * @param value - Raw input string
 * @returns Formatted phone number for display in input
 */
export function formatPhoneInput(value: string | undefined | null): string {
  if (!value) return '';

  const digits = value.replace(/\D/g, '').slice(0, 10);

  if (digits.length > 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length > 3) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }

  return digits;
}

export function formatZipCodeInput(value: string | undefined | null): string {
  if (!value) return '';

  // Strip all non-digits and limit to 9
  const digits = value.replace(/\D/g, '').slice(0, 9);

  // Auto-insert hyphen after 5 digits
  if (digits.length > 5) {
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  }

  return digits;
}
